import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Shield, Key, Lock, Mail, Globe, Plus, X,
  Loader2, Save, Check, AlertCircle, ExternalLink, TestTube,
  Building2, Fingerprint, UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';

const TenantAuthPage = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Security config state
  const [securityConfig, setSecurityConfig] = useState({
    password_enabled: true,
    microsoft_sso_enabled: false,
    microsoft_tenant_id: '',
    microsoft_client_id: '',
    saml_enabled: false,
    saml_entity_id: '',
    saml_sso_url: '',
    saml_certificate: '',
    mfa_enabled: false,
    mfa_required: false,
    domain_restrictions: []
  });
  
  const [originalConfig, setOriginalConfig] = useState({});
  const [newDomain, setNewDomain] = useState('');

  useEffect(() => {
    loadTenantConfig();
  }, [tenantId]);

  useEffect(() => {
    const changed = JSON.stringify(securityConfig) !== JSON.stringify(originalConfig);
    setHasChanges(changed);
  }, [securityConfig, originalConfig]);

  const loadTenantConfig = async () => {
    setLoading(true);
    try {
      const tenantResponse = await api.getTenant(tenantId);
      setTenant(tenantResponse.data);
      
      const configResponse = await api.getTenantConfig(tenantId);
      const config = configResponse.data.security_config || {};
      
      const fullConfig = {
        password_enabled: config.password_enabled ?? true,
        microsoft_sso_enabled: config.microsoft_sso_enabled ?? false,
        microsoft_tenant_id: config.microsoft_tenant_id || '',
        microsoft_client_id: config.microsoft_client_id || '',
        saml_enabled: config.saml_enabled ?? false,
        saml_entity_id: config.saml_entity_id || '',
        saml_sso_url: config.saml_sso_url || '',
        saml_certificate: config.saml_certificate || '',
        mfa_enabled: config.mfa_enabled ?? false,
        mfa_required: config.mfa_required ?? false,
        domain_restrictions: config.domain_restrictions || []
      };
      
      setSecurityConfig(fullConfig);
      setOriginalConfig(fullConfig);
    } catch (err) {
      console.error('Failed to load tenant config:', err);
      toast.error('Failed to load tenant configuration');
      navigate('/platform/tenants');
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = (field, value) => {
    setSecurityConfig(prev => ({ ...prev, [field]: value }));
  };

  const addDomain = () => {
    if (!newDomain.trim()) return;
    
    // Validate domain format
    const domain = newDomain.trim().toLowerCase();
    if (!domain.startsWith('@')) {
      toast.error('Domain must start with @');
      return;
    }
    
    if (securityConfig.domain_restrictions.includes(domain)) {
      toast.error('Domain already added');
      return;
    }
    
    setSecurityConfig(prev => ({
      ...prev,
      domain_restrictions: [...prev.domain_restrictions, domain]
    }));
    setNewDomain('');
  };

  const removeDomain = (domain) => {
    setSecurityConfig(prev => ({
      ...prev,
      domain_restrictions: prev.domain_restrictions.filter(d => d !== domain)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateTenantConfig(tenantId, {
        security_config: securityConfig
      });
      
      setOriginalConfig(securityConfig);
      toast.success('Authentication settings saved successfully');
    } catch (err) {
      console.error('Failed to save config:', err);
      toast.error('Failed to save authentication settings');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (provider) => {
    setTesting(provider);
    try {
      // Simulate test - in production this would verify the connection
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      if (provider === 'microsoft') {
        if (!securityConfig.microsoft_tenant_id || !securityConfig.microsoft_client_id) {
          toast.error('Please configure Microsoft Tenant ID and Client ID first');
          return;
        }
      } else if (provider === 'saml') {
        if (!securityConfig.saml_sso_url || !securityConfig.saml_entity_id) {
          toast.error('Please configure SAML Entity ID and SSO URL first');
          return;
        }
      }
      
      toast.success(`${provider === 'microsoft' ? 'Microsoft' : 'SAML'} connection test successful`);
    } catch (err) {
      toast.error(`Connection test failed: ${err.message}`);
    } finally {
      setTesting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Platform Admin Context Banner */}
        <div className="mb-4 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 flex items-center gap-3">
          <Shield className="w-5 h-5 text-slate-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-slate-700">Platform Admin</p>
            <p className="text-xs text-slate-500">
              You are configuring authentication as a <strong>Platform Administrator</strong>. 
              These settings control tenant-level security policies.
            </p>
          </div>
        </div>
        
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/platform/tenants')}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
            data-testid="back-to-tenants"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Tenant Directory
          </button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                <Key className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">SSO & Authentication</h1>
                <p className="text-sm text-gray-500">{tenant?.tenant?.name || 'Loading...'}</p>
              </div>
            </div>
            
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                hasChanges 
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700' 
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
              data-testid="save-auth-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
        
        {/* Changes indicator */}
        {hasChanges && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-600" />
            <span className="text-sm text-yellow-700">You have unsaved changes</span>
          </div>
        )}
        
        {/* Authentication Providers */}
        <div className="space-y-6">
          {/* Email/Password */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Email/Password</h3>
                    <p className="text-sm text-gray-500">Standard username and password authentication</p>
                  </div>
                </div>
                
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={securityConfig.password_enabled}
                    onChange={(e) => updateConfig('password_enabled', e.target.checked)}
                    className="sr-only peer"
                    data-testid="toggle-password"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>
            </div>
            
            {securityConfig.password_enabled && (
              <div className="px-5 py-4 bg-gray-50 text-sm text-gray-600">
                <p>Users can sign in with their email address and password.</p>
              </div>
            )}
          </div>
          
          {/* Microsoft SSO */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Microsoft Login (OIDC)</h3>
                    <p className="text-sm text-gray-500">Sign in with Microsoft 365 / Azure AD</p>
                  </div>
                </div>
                
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={securityConfig.microsoft_sso_enabled}
                    onChange={(e) => updateConfig('microsoft_sso_enabled', e.target.checked)}
                    className="sr-only peer"
                    data-testid="toggle-microsoft-sso"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
            
            {securityConfig.microsoft_sso_enabled && (
              <div className="px-5 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Azure AD Tenant ID
                  </label>
                  <input
                    type="text"
                    value={securityConfig.microsoft_tenant_id}
                    onChange={(e) => updateConfig('microsoft_tenant_id', e.target.value)}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    data-testid="microsoft-tenant-id"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Application (Client) ID
                  </label>
                  <input
                    type="text"
                    value={securityConfig.microsoft_client_id}
                    onChange={(e) => updateConfig('microsoft_client_id', e.target.value)}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    data-testid="microsoft-client-id"
                  />
                </div>
                
                <div className="flex items-center justify-between pt-2">
                  <a 
                    href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Azure Portal
                  </a>
                  
                  <button
                    onClick={() => testConnection('microsoft')}
                    disabled={testing === 'microsoft'}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    data-testid="test-microsoft-btn"
                  >
                    {testing === 'microsoft' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <TestTube className="w-4 h-4" />
                    )}
                    Test Connection
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* SAML 2.0 */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">SAML 2.0</h3>
                    <p className="text-sm text-gray-500">Enterprise single sign-on with SAML identity providers</p>
                  </div>
                </div>
                
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={securityConfig.saml_enabled}
                    onChange={(e) => updateConfig('saml_enabled', e.target.checked)}
                    className="sr-only peer"
                    data-testid="toggle-saml"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-purple-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
            </div>
            
            {securityConfig.saml_enabled && (
              <div className="px-5 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Entity ID (Issuer)
                  </label>
                  <input
                    type="text"
                    value={securityConfig.saml_entity_id}
                    onChange={(e) => updateConfig('saml_entity_id', e.target.value)}
                    placeholder="https://idp.example.com/saml2/metadata"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    data-testid="saml-entity-id"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SSO URL
                  </label>
                  <input
                    type="text"
                    value={securityConfig.saml_sso_url}
                    onChange={(e) => updateConfig('saml_sso_url', e.target.value)}
                    placeholder="https://idp.example.com/saml2/sso"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    data-testid="saml-sso-url"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    X.509 Certificate (PEM format)
                  </label>
                  <textarea
                    value={securityConfig.saml_certificate}
                    onChange={(e) => updateConfig('saml_certificate', e.target.value)}
                    placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-xs"
                    data-testid="saml-certificate"
                  />
                </div>
                
                <div className="flex justify-end pt-2">
                  <button
                    onClick={() => testConnection('saml')}
                    disabled={testing === 'saml'}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    data-testid="test-saml-btn"
                  >
                    {testing === 'saml' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <TestTube className="w-4 h-4" />
                    )}
                    Test Connection
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* MFA Settings */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Fingerprint className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Multi-Factor Authentication</h3>
                    <p className="text-sm text-gray-500">Require additional verification for sign-in</p>
                  </div>
                </div>
                
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={securityConfig.mfa_enabled}
                    onChange={(e) => updateConfig('mfa_enabled', e.target.checked)}
                    className="sr-only peer"
                    data-testid="toggle-mfa"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-amber-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600"></div>
                </label>
              </div>
            </div>
            
            {securityConfig.mfa_enabled && (
              <div className="px-5 py-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={securityConfig.mfa_required}
                    onChange={(e) => updateConfig('mfa_required', e.target.checked)}
                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                    data-testid="mfa-required"
                  />
                  <div>
                    <span className="font-medium text-gray-900">Require MFA for all users</span>
                    <p className="text-sm text-gray-500">Users must set up MFA before accessing the platform</p>
                  </div>
                </label>
              </div>
            )}
          </div>
          
          {/* Domain Restrictions */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Domain Restrictions</h3>
                  <p className="text-sm text-gray-500">Limit sign-ups to specific email domains</p>
                </div>
              </div>
            </div>
            
            <div className="px-5 py-4 space-y-4">
              {/* Domain list */}
              {securityConfig.domain_restrictions.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {securityConfig.domain_restrictions.map((domain) => (
                    <span 
                      key={domain}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal-50 text-teal-700 rounded-full text-sm"
                    >
                      {domain}
                      <button
                        onClick={() => removeDomain(domain)}
                        className="p-0.5 hover:bg-teal-100 rounded-full"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  No domain restrictions. All email domains are allowed.
                </p>
              )}
              
              {/* Add domain */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="@company.com"
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && addDomain()}
                  data-testid="new-domain-input"
                />
                <button
                  onClick={addDomain}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2"
                  data-testid="add-domain-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>
              
              <p className="text-xs text-gray-500">
                When domain restrictions are set, only users with email addresses from these domains can sign up or be invited.
              </p>
            </div>
          </div>
        </div>
        
        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Authentication Notes
          </h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>At least one authentication method must remain enabled.</li>
            <li>Microsoft SSO requires app registration in Azure Portal.</li>
            <li>SAML configuration requires coordination with your identity provider.</li>
            <li>Domain restrictions apply to new sign-ups and invitations.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TenantAuthPage;
